/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import {
    AnalyticsConstants,
    Diagnostics,
    Operations,
    Payload,
} from '@tivocorp/tivo-webapp-loggers';

import { DeviceModel } from '../../core/device/DeviceModel';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { LpExpiredError } from '../../core/error/LpExpiredError';
import { DefaultServicesClientImpl } from '../../core/impls/DefaultServiceClientImpl';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { VuLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { CursorManager } from '../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { QrCodeGenerator } from '../../core/utils';
import { CcbSwitch } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { setSignInButtonHighlightAction } from '../../store/signin/signin.actions';
import { onLpCodeExpiration, showSignInErrorOverlay } from '../../store/signin/signin.reducers';
import { NoNetworkOverlay } from '../NoNetworkOverlay';
// TODO: need to move Spinner component to a common place
import { Spinner } from '../VideoPlayerUI/Spinner';
import { Button } from './Button';
import { CommandmentMessage } from './CommandmentMessage';

import './SignInScreen.scss';

const SignInScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'SignInScreen';
    const dispatch = useTivoDispatch();
    const { error, hasError, licensePlateCode, licensePlateUrl } = useTivoSelector(
        (state: RootState) => state.signin,
    );
    const hasPageRendered = useRef(false);

    const isSpinnerActive = useTivoSelector((state: RootState) => state.spinner.isSpinnerActive);

    const { isOverlayDialogActive, uiProperties } = useTivoSelector(
        (state: RootState) => state.overlayDialog,
    );

    const { isButtonHighlight } = useTivoSelector((state: RootState) => state.signin);

    const [isCodeExpired, setIsCodeExpired] = useState(false);

    const displayName =
        new DefaultServicesClientImpl().getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME) ||
        DeviceModel.getTiVoSettings().getDisplayName();

    const signinText = `${Locale.getString(LocaleKeys.SIGN_IN_TO)} ${displayName}`;
    const isNoNetworkOverlayActive =
        isOverlayDialogActive && uiProperties.overlayType === OverlayTypes.NO_NETWORK;
    const showErrorOverlay: boolean =
        !isNoNetworkOverlayActive && hasError && !(error instanceof LpExpiredError);

    useEffect(() => {
        VuLogger.logSignInScreenEntryEvent();
        return () => {
            VuLogger.logSignInScreenExitEvent();
        };
    }, []);

    useEffect(() => {
        if (showErrorOverlay) {
            dispatch(showSignInErrorOverlay(error));
        }
    }, [showErrorOverlay]);

    useEffect(() => {
        if (hasPageRendered.current) {
            if (hasError) {
                if (error instanceof LpExpiredError) {
                    setIsCodeExpired(true);
                    dispatch(onLpCodeExpiration());
                }
            } else {
                dispatch(hideOverlayDialog());
                setIsCodeExpired(false);
                Log.i(TAG, 'useEffect[hasError]: hasError is false.');
                Diagnostics.info(TAG, 'useEffect[hasError]: hasError is false.');
            }
        }
        hasPageRendered.current = true;
    }, [hasError]);

    const createLicensePlateComponentWithQR = (qrSrc: string) => {
        return (
            <>
                <div className="license-plate-container">
                    {/* QR Section */}
                    <div className="qr-code-container" data-testid="qr-code-container">
                        <span className="qr-code-text">
                            {Locale.getString(LocaleKeys.SIGN_IN_SCAN_MESSAGE)}
                        </span>
                        <div className="qr-code">
                            <img className="qr-code-image" src={qrSrc} />
                        </div>
                    </div>

                    {/* OR Section */}
                    <div className="or-text-container">
                        <span className="line" />
                        <span className="or-text">
                            {Locale.getString(LocaleKeys.SIGN_IN_OR_MESSAGE)}
                        </span>
                        <span className="line" />
                    </div>

                    {/* License Plate Section */}
                    <div className="license-plate-link-container">
                        <div className="link-container" data-testid="go-to-link-container">
                            <CommandmentMessage
                                action={licensePlateUrl}
                                message={Locale.getString(LocaleKeys.SIGN_IN_GO_TO)}
                                type="qr-code-link"
                            />
                        </div>
                    </div>
                </div>
                <div className="license-plate-code-container">
                    <span className="license-plate-code-text">
                        {Locale.getString(LocaleKeys.SIGN_IN_ACTIVATION_CODE_DESCRIPTION)}
                    </span>
                    <span className="license-plate-code">{licensePlateCode}</span>
                </div>
                <div
                    className="refresh-screen-message-container"
                    data-testid="refresh-message-container"
                >
                    <CommandmentMessage
                        message={Locale.getString(LocaleKeys.SIGN_IN_REFRESH_MESSAGE)}
                    />
                </div>
            </>
        );
    };

    const createLicensePlateComponentWithoutQR = () => {
        return (
            <>
                <div
                    className="active-code-content-container"
                    data-testid="active-code-content-container"
                >
                    <div className="go-to-link-container" data-testid="go-to-link-container">
                        <CommandmentMessage
                            action={licensePlateUrl}
                            message={Locale.getString(LocaleKeys.SIGN_IN_GO_TO)}
                            type="link"
                        />
                    </div>
                    <div className="code-container" data-testid="code-container">
                        <CommandmentMessage
                            action={licensePlateCode}
                            message={Locale.getString(LocaleKeys.SIGN_IN_AND_ENTER)}
                            type="code"
                        />
                    </div>
                    <div
                        className="refresh-message-container"
                        data-testid="refresh-message-container"
                    >
                        <CommandmentMessage
                            message={Locale.getString(LocaleKeys.SIGN_IN_REFRESH_MESSAGE)}
                        />
                    </div>
                </div>
            </>
        );
    };

    const createActiveCodeContentComponents = () => {
        const isSimplifiedSignInEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_SIMPLIFIED_SIGN_IN_REMOVE_IN_KST_12173,
        );

        let qrCodeSrc = '';

        try {
            qrCodeSrc = QrCodeGenerator.generateQrCode(licensePlateUrl, 10, 1).bitmap;
        } catch (qrError) {
            Log.e(TAG, 'Failed to generate QR code', qrError as Error);
            Diagnostics.error(TAG, 'Failed to generate QR code', qrError as Error);
            const payload = new Payload();
            payload.add(AnalyticsConstants.Fields.EVENT_NAME, 'QR_GENERATION_FAILED');
            payload.add(AnalyticsConstants.Fields.EVENT_TYPE, 'ERROR');
            payload.add(AnalyticsConstants.Fields.MESSAGE, JSON.stringify(qrError));
            Operations.log(payload);
        }

        return isSimplifiedSignInEnabled && qrCodeSrc
            ? createLicensePlateComponentWithQR(qrCodeSrc)
            : createLicensePlateComponentWithoutQR();
    };

    const [isCursorEnabled, setIsCursorEnabled] = useState(false);

    const onCursorStateChangeCallback = (state) => {
        dispatch(setSignInButtonHighlightAction(!state));
        setIsCursorEnabled(state);
    };

    useEffect(() => {
        const ref = CursorManager.subscribe(onCursorStateChangeCallback);
        return () => {
            CursorManager.unsubscribe(ref);
        };
    }, []);

    const createExpiredCodeContentComponents = () => {
        return (
            <div
                className="expired-code-content-container"
                data-testid="expired-code-content-container"
            >
                <div className="expired-message-container" data-testid="expired-message-container">
                    <CommandmentMessage
                        message={Locale.getString(LocaleKeys.SIGN_IN_CODE_EXPIRED_MESSAGE)}
                    />
                </div>
                <div
                    className="get-new-code-btn-container"
                    data-testid="get-new-code-btn-container"
                >
                    <Button
                        highlighted={
                            (!isCursorEnabled || isButtonHighlight) && !isOverlayDialogActive
                        }
                    >
                        {Locale.getString(LocaleKeys.SIGN_IN_NEW_ACTIVATION_BUTTON_LABEL)}
                    </Button>
                </div>
            </div>
        );
    };

    const createScreenContent = () => {
        const providerClasses = isCodeExpired
            ? 'provider-app-name expired-code'
            : 'provider-app-name';

        return (
            licensePlateCode && (
                <React.Fragment>
                    <div className={providerClasses} data-testid="provider-app-text">
                        {signinText}
                    </div>
                    {(isCodeExpired && createExpiredCodeContentComponents()) ||
                        createActiveCodeContentComponents()}
                </React.Fragment>
            )
        );
    };

    return (
        <React.Fragment>
            <NoNetworkOverlay />
            <div className="signin-screen-container" data-testid="signin-screen-container">
                {isSpinnerActive ? (
                    <Spinner message={Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)} />
                ) : (
                    !showErrorOverlay && createScreenContent()
                )}
            </div>
        </React.Fragment>
    );
};

export default SignInScreen;
